import axios from 'axios';
import { API_BASE_URL } from '../config';

/**
 * Admin service helpers to aggregate counts and datasets for the Admin Dashboard.
 * Uses existing backend endpoints discovered via swagger and code references.
 * Note: Some aggregate metrics (e.g., total quotes across all jobs, total messages)
 * do not have direct endpoints; we surface what&#39;s available and leave placeholders.
 */

export const fetchHomeUsers = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/HomeUsers/getHomeUsers`);
    const list = Array.isArray(data?.model) ? data.model : data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('fetchHomeUsers failed', err);
    return [];
  }
};

export const fetchBusinessUsers = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/BusinessUser/getBusinessUsers`);
    const list = Array.isArray(data?.model) ? data.model : data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('fetchBusinessUsers failed', err);
    return [];
  }
};

export const fetchLatestJobs = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/Jobs/GetLatestJobs`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('fetchLatestJobs failed', err);
    return [];
  }
};

export const fetchQuoteTemplates = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/QuoteTemplate`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('fetchQuoteTemplates failed', err);
    return [];
  }
};

/**
 * Not in swagger, but referenced by SocialProofEngine: /api/activity/getlatest
 * Returns latest social proof/interaction items.
 */
export const fetchLatestInteractions = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/activity/getlatest`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('fetchLatestInteractions not available or failed', err);
    return [];
  }
};

/**
 * Subscription types endpoint discovered in swagger:
 * GET /api/Subscription/getSubscriptionTypes?mtype=&bizid=
 * Without a specific business id, we can only list available types, not "valid subscriptions" across all businesses.
 */
export const fetchSubscriptionTypes = async (mtype, bizid) => {
  try {
    // Build query string conditionally
    const params = new URLSearchParams();
    if (mtype) params.append('mtype', mtype);
    if (typeof bizid !== 'undefined' && bizid !== null) params.append('bizid', String(bizid));
    const qs = params.toString();
    const url = `/api/Subscription/getSubscriptionTypes${qs ? `?${qs}` : ''}`;
    const { data } = await axios.get(`${API_BASE_URL}${url}`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('fetchSubscriptionTypes failed', err);
    return [];
  }
};

/**
 * Professionals and Services catalogs
 */
export const fetchProfessionals = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/Professional/getProfessionals`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('fetchProfessionals failed', err);
    return [];
  }
};

export const fetchServices = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/Professional/getServices`);
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('fetchServices failed', err);
    return [];
  }
};

/**
 * Try to use aggregate admin stats endpoint first.
 */
export const fetchAdminStatsAggregate = async () => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/api/Admin/stats`);
    return data || null;
  } catch (err) {
    // Silently ignore to allow fallback path
    return null;
  }
};

/**
 * Aggregated stats for dashboard tiles.
 * Returns a normalized structure the UI can render easily.
 */
export const fetchAdminStats = async () => {
  // Prefer backend aggregate endpoint if available
  const dto = await fetchAdminStatsAggregate();
  if (dto) {
    return {
      counts: {
        homeUsers: dto.totalHomeUsers || 0,
        businessUsers: dto.totalBusinessUsers || 0,
        jobsTotal: dto.totalJobs || 0,
        quotesTotal: dto.totalQuotes || 0,
        businessResponses: dto.totalBusinessResponses || 0,
        messages: dto.totalMessages || 0,
        conversations: dto.totalConversations || 0,
        projects: dto.totalProjects || 0,
        portfolios: dto.totalPortfolios || 0,
        reviews: dto.totalReviews || 0,
        activities: dto.totalActivities || 0,
        subscriptionsTotal: dto.totalSubscriptions || 0,
        subscriptionsActive: dto.activeSubscriptions || 0,
        revenueTotal: dto.revenueTotal || 0,
        revenueLast30Days: dto.revenueLast30Days || 0,
      },
      datasets: {
        subscriptionsByType: dto.subscriptionsByType || [],
        subscriptionsByMType: dto.subscriptionsByMType || [],
      },
      notes: {
        jobs: 'Jobs total via Admin stats endpoint.',
        quotes: 'Total quotes via Admin stats endpoint.',
        messages: 'Total messages via Admin stats endpoint.',
        subscriptions: 'Subscription totals and revenue via Admin stats endpoint.',
      },
    };
  }

  // Fallback to multi-call approach
  const [homeUsers, businessUsers, latestJobs, quoteTemplates, interactions, professionals, services] = await Promise.all([
    fetchHomeUsers(),
    fetchBusinessUsers(),
    fetchLatestJobs(),
    fetchQuoteTemplates(),
    fetchLatestInteractions(),
    fetchProfessionals(),
    fetchServices(),
  ]);

  // Subscriptions: optional query to see available types (not "active subscriptions" system-wide)
  const subscriptionTypes = await fetchSubscriptionTypes();

  return {
    counts: {
      homeUsers: homeUsers.length,
      businessUsers: businessUsers.length,
      jobsLatest: latestJobs.length, // Note: "latest jobs" count, not total jobs system-wide
      quoteTemplates: quoteTemplates.length, // Proxy for quote-related artifacts
      interactions: interactions.length,
      professionals: professionals.length,
      services: services.length,
      subscriptionTypes: subscriptionTypes.length,
      // messages, quotes total, revenue from valid subscriptions would need dedicated admin endpoints
    },
    datasets: {
      homeUsers,
      businessUsers,
      latestJobs,
      quoteTemplates,
      interactions,
      professionals,
      services,
      subscriptionTypes,
    },
    notes: {
      jobs: 'Jobs count reflects latest jobs endpoint. Total jobs across system requires a dedicated admin endpoint.',
      quotes: 'Total quotes across all jobs require a dedicated endpoint; showing quote templates count as proxy.',
      messages: 'Aggregate message counts require admin endpoints; chat APIs are scoped by conversation.',
      subscriptions: 'Revenue and count of valid subscriptions require admin endpoints; getSubscriptionTypes lists type catalog.',
    },
  };
};
